import ServiceJobsPanel from './ServiceJobsPanel.jsx';
import { useCallback, useEffect, useRef, useState } from "react";
import { organizationContext } from "../utils/organizationContext.js";
import { endpoints,tokenStore } from "../api.js";
import { t } from "../i18n.js";
import "../serviceQuotes.css";

const idOf = row => String(row?._id || "");
const invalidResponse = () => new Error("The server returned an invalid response. Please try again.");
const newKey = () => crypto.randomUUID();
const freshForm = { serviceRequestId: "", amountOMR: "", timelineDays: "0", message: "" };
const actionText = { accepted: "Accept quote", rejected: "Reject quote", withdrawn: "Withdraw quote" };
const statusText = { submitted: "Submitted", accepted: "Accepted", rejected: "Rejected", withdrawn: "Withdrawn" };

function QuotePanel({ language = "en", services = endpoints, initialMode="customer",providerOnly=false }) {
  const [mode, setMode] = useState(initialMode);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestId, setRequestId] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [canSubmit, setCanSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [itemErrors, setItemErrors] = useState({});
  const [pending, setPending] = useState("");
  const [form, setForm] = useState(freshForm);
  const [formError, setFormError] = useState("");
  const generation = useRef(0);
  const alive = useRef(false);
  const lock = useRef(false);
  const submission = useRef(null);
  const cards = useRef(new Map());
  const liveNotice = useRef(null);
  const localError = failure => {
    const message = failure.message || "Request failed";
    if (failure.status === 503) return "Quote service is temporarily unavailable. Please try again.";
    if (t("ar", message) === message && [400, 403, 404, 409].includes(failure.status)) {
      return ({ 400: "Check the quote fields and try again.", 403: "You cannot make this quote decision.", 404: "Service quote was not found.", 409: "This quote has changed. Refresh the quotes before deciding." })[failure.status];
    }
    return message;
  };
  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    if(providerOnly){setRequestsLoading(false);return()=>{alive.current=false;generation.current+=1;};}
    setRequestsLoading(true);
    services.workspaceList("serviceRequests").then(rows => {
      if (!Array.isArray(rows)) throw invalidResponse();
      if (!cancelled) setRequests(rows);
    }).catch(failure => { if (!cancelled) setError(localError(failure)); })
      .finally(() => { if (!cancelled) setRequestsLoading(false); });
    return () => { cancelled = true; alive.current = false; generation.current += 1; };
  }, [services,providerOnly]);
  const load = useCallback(async () => {
    if (lock.current) return;
    const version = ++generation.current;
    setError(""); setItemErrors({}); setNotice(""); setLoading(true);
    try {
      const result = mode === "provider" ? await services.myServiceQuotes() : requestId ? await services.requestQuotes(requestId) : [];
      const rows = mode === "provider" ? result?.items : result;
      if (!Array.isArray(rows)) throw invalidResponse();
      if (mode === "provider" && typeof result.canSubmit !== "boolean") throw invalidResponse();
      if (alive.current && generation.current === version) { setQuotes(rows); setCanSubmit(mode === "provider" && result.canSubmit); }
    } catch (failure) {
      if (alive.current && generation.current === version) { setQuotes([]); setCanSubmit(false); setError(localError(failure)); }
    } finally { if (alive.current && generation.current === version) setLoading(false); }
  }, [mode, requestId, services]);
  useEffect(() => { setQuotes([]); setCanSubmit(false); load(); }, [load]);

  async function decide(quote, status) {
    if (lock.current) return;
    lock.current = true; setPending(idOf(quote)); setNotice(""); setItemErrors({});
    try {
      const changed = await services.decideServiceQuote(idOf(quote), { status, _expectedUpdated: quote._expectedUpdated ?? quote.updated ?? 0 });
      if (idOf(changed) !== idOf(quote) || changed.status !== status) throw invalidResponse();
      if (!alive.current) return;
      setQuotes(rows => rows.map(row => idOf(row) === idOf(quote) ? changed : row));
      setNotice("Quote decision saved.");
      requestAnimationFrame(() => cards.current.get(idOf(quote))?.focus());
    } catch (failure) {
      if (alive.current) {
        setItemErrors({ [idOf(quote)]: localError(failure) });
        requestAnimationFrame(() => cards.current.get(idOf(quote))?.focus());
      }
    } finally { lock.current = false; if (alive.current) setPending(""); }
  }
  async function submit(event) {
    event.preventDefault(); if (lock.current) return;
    const amount = Number(form.amountOMR);
    const timelineDays = Number(form.timelineDays);
    if (!/^\d+(?:\.\d{1,3})?$/.test(form.amountOMR) || amount <= 0 || !Number.isSafeInteger(Math.round(amount * 1000))) {
      setFormError("Enter a positive OMR amount with at most three decimal places."); return;
    }
    if (!/^\d+$/.test(form.timelineDays) || !Number.isSafeInteger(timelineDays)) {
      setFormError("Enter a non-negative whole number of days."); return;
    }
    if (!submission.current) submission.current = newKey();
    generation.current += 1; setLoading(false);
    lock.current = true; setPending("submit"); setFormError(""); setNotice("");
    try {
      const created = await services.createServiceQuote(form.serviceRequestId.trim(), { amountOMR: amount, timelineDays, message: form.message, submissionKey: submission.current });
      if (!idOf(created)) throw invalidResponse();
      if (!alive.current) return;
      setQuotes(rows => [created, ...rows.filter(row => idOf(row) !== idOf(created))]);
      setForm(freshForm); submission.current = null;
      setNotice("Quote submitted.");
      requestAnimationFrame(() => liveNotice.current?.focus());
    } catch (failure) { if (alive.current) setFormError(localError(failure)); }
    finally { lock.current = false; if (alive.current) setPending(""); }
  }
  const change = key => event => {
    const value = event.target.value;
    submission.current = null;
    setForm(current => ({ ...current, [key]: value }));
  };
  return <section className="service-quotes" aria-label={t(language, "Service quotes")} dir={language === "ar" ? "rtl" : "ltr"}>
    <header><h2>{t(language, "Service quotes")}</h2><button type="button" onClick={load} disabled={loading || !!pending}>{t(language, "Refresh quotes")}</button></header>
    {!providerOnly&&<div className="quote-tabs"><button type="button" aria-pressed={mode === "customer"} disabled={!!pending} onClick={() => setMode("customer")}>{t(language, "Quotes for my requests")}</button><button type="button" aria-pressed={mode === "provider"} disabled={!!pending} onClick={() => setMode("provider")}>{t(language, "My provider quotes")}</button></div>}
    {mode === "customer" && <label>{t(language, "Choose a service request")}<select value={requestId} disabled={!!pending || requestsLoading || !requests.length} onChange={event => setRequestId(event.target.value)}><option value="">{t(language, "Choose a service request")}</option>{requests.map(row => <option key={idOf(row)} value={idOf(row)}>{t(language, row.category)} · {`\u2068${idOf(row)}\u2069`}</option>)}</select></label>}
    {error && <p role="alert">{t(language, error)}</p>}
    {(loading || (mode === "customer" && requestsLoading)) && <p role="status">{t(language, "Loading quotes…")}</p>}
    {notice && <p role="status" tabIndex={-1} ref={liveNotice}>{t(language, notice)}</p>}
    {!loading && !requestsLoading && !error && !quotes.length && <p>{t(language, mode === "customer" && !requests.length ? "No service requests yet. Create a request above." : mode === "customer" && !requestId ? "Select your request to view its quotes." : "No quotes to display.")}</p>}
    <div className="quote-list" aria-busy={loading}>{quotes.map(quote => <article key={idOf(quote)} tabIndex={-1} ref={node => node ? cards.current.set(idOf(quote), node) : cards.current.delete(idOf(quote))}>
      <h3>{t(language, "Quote reference")} <bdi>{idOf(quote)}</bdi></h3>
      <p>{t(language, "Service request reference")} <bdi>{quote.serviceRequestId}</bdi></p>
      <p><bdi>{Number.isFinite(quote.amountOMR) ? new Intl.NumberFormat(language === "ar" ? "ar-OM" : "en-OM", { style: "currency", currency: "OMR" }).format(quote.amountOMR) : t(language, "Amount unavailable")}</bdi> · {Number.isSafeInteger(quote.timelineDays) && quote.timelineDays >= 0 ? `${quote.timelineDays} ${t(language, "days")}` : t(language, "Timeline unavailable")}</p>
      <p>{t(language, statusText[quote.status] || quote.status)}</p><p dir="auto">{quote.message}</p>
      <div className="quote-actions">{(quote.actions || []).filter(action => actionText[action]).map(action => <button key={action} type="button" disabled={!!pending || loading} aria-busy={pending === idOf(quote)} onClick={() => decide(quote, action)}>{t(language, pending === idOf(quote) ? "Saving quote…" : actionText[action])}</button>)}</div>
      {itemErrors[idOf(quote)] && <p role="alert">{t(language, itemErrors[idOf(quote)])}</p>}
    </article>)}</div>
    {mode === "provider" && canSubmit && <form onSubmit={submit} aria-busy={pending === "submit"}>
      <h3>{t(language, "Submit a quote")}</h3>
      <label>{t(language, "Service request reference")}<input dir="ltr" value={form.serviceRequestId} onChange={change("serviceRequestId")} maxLength={100} required disabled={!!pending} /></label>
      <div className="quote-fields"><label>{t(language, "Quote amount (OMR)")}<input type="number" dir="ltr" min="0.001" step="0.001" required value={form.amountOMR} onChange={change("amountOMR")} disabled={!!pending} /></label><label>{t(language, "Timeline (days)")}<input type="number" dir="ltr" min="0" step="1" required value={form.timelineDays} onChange={change("timelineDays")} disabled={!!pending} /></label></div>
      <label>{t(language, "Quote message")}<textarea dir="auto" maxLength={1500} value={form.message} onChange={change("message")} disabled={!!pending} /></label>
      {formError && <p role="alert">{t(language, formError)}</p>}
      <button type="submit" disabled={!!pending}>{t(language, pending === "submit" ? "Submitting quote…" : "Submit a quote")}</button>
    </form>}
    {mode === "provider" && !loading && !error && !canSubmit && <p>{t(language, "An active account linked to an approved service provider is required.")}</p>}
    {services.serviceJobs&&<ServiceJobsPanel key={mode} language={language} services={services} mode={mode} quotes={quotes}/>}
  </section>;
}

export default function ServiceQuotesPanel(props){const[revision,set]=useState(()=>organizationContext.version(tokenStore.version()));useEffect(()=>{const update=()=>set(organizationContext.version(tokenStore.version()));window.addEventListener("maywade-organization-changed",update);window.addEventListener("maywade-auth-changed",update);return()=>{window.removeEventListener("maywade-organization-changed",update);window.removeEventListener("maywade-auth-changed",update);};},[]);return <QuotePanel {...props} key={revision}/>;}
